import { useEffect, useRef } from 'react';
import { RoomLostOverlay } from '../ui/RoomLostOverlay.js';
import { useRoom } from '../net/useRoom.js';
import { createDriver } from '../driver/index.js';
import { loadSession } from '../net/session.js';
import { JoinScreen, NarrationBanner, HandGameView } from '../hand/index.js';

/**
 * Play (`/play`) — the Hand: private per-phone controller.
 *
 * Routing logic:
 * 1. No driver attached → show JoinScreen.
 * 2. Driver attached but no privateView yet → show LobbyScreen (waiting for DEAL).
 * 3. Once phase + privateView arrive, route to the correct hand screen.
 *
 * Session/reconnect: on mount, if localStorage holds a session token for ?code=
 * param, auto-attempt REJOIN via MockDriver (Wave 2 swaps to LiveSocketDriver).
 */
export default function Play() {
  const { attach, publicView, privateView, narration, playerId, driver, send } = useRoom();
  // Server restarted / room swept — the board on screen is dead (see ui/RoomLostOverlay).
  const roomLost = useRoom((s) => s.roomLost);
  const didMount = useRef(false);

  useEffect(() => {
    if (didMount.current) return;
    didMount.current = true;

    // Attempt session reconnect first.
    const params = new URLSearchParams(window.location.search);
    const codeParam = params.get('code');

    if (codeParam) {
      const stored = loadSession(codeParam.toUpperCase());
      if (stored) {
        // Reconnect: attach driver with the stored session so the live driver
        // auto-REJOINs on open (mock resends from its scripted identity).
        const d = createDriver(stored.playerId);
        attach(d, { role: 'hand', code: stored.code, sessionToken: stored.sessionToken });
        // Explicit REJOIN too (mock relies on it; live also tolerates it).
        setTimeout(() => {
          send({ type: 'REJOIN', code: stored.code, sessionToken: stored.sessionToken });
        }, 0);
        return;
      }
    }

    // No stored session — attach a fresh driver in pre-join mode so the store is
    // ready to receive messages. JoinScreen then sends JOIN_ROOM. `p_cara` is a
    // mock-only default (the live server assigns real identity on JOIN).
    const d = createDriver('p_cara');
    attach(d, { role: 'hand' });
  }, [attach, send]);

  const phase = publicView?.phase ?? null;

  // Player's own name from public roster.
  const myName = publicView?.players.find((p) => p.id === playerId)?.name ?? '';

  if (roomLost) return <RoomLostOverlay />;

  // Guard: if no playerId yet, we're in the Join flow.
  if (!playerId) {
    return (
      <>
        <JoinScreen />
        {narration && <NarrationBanner narration={narration} />}
      </>
    );
  }

  // Guard: if we have a playerId but no phase/view yet (transient), show loading.
  if (!phase || !publicView || !privateView) {
    return (
      <div
        style={{
          minHeight: '100dvh',
          display: 'grid',
          placeItems: 'center',
          background: 'var(--bg-gradient)',
          color: 'var(--text-secondary)',
          fontFamily: 'var(--font-ui)',
        }}
      >
        Connecting…
      </div>
    );
  }

  // All phases route through the shared HandGameView (also used by the Host).
  // Players get the same GameHeader (phase label + timer) as the host.
  return (
    <HandGameView
      publicView={publicView}
      privateView={privateView}
      narration={narration}
      myName={myName}
      showHeader
    />
  );
}
